import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { renderBlocks } from '../../renderer/BlockRenderer.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { marginFields, visibilityField, weightField } from '../_shared/blockFields.js';
import { MaterialIcon, MaterialSymbolsFont, iconRenders } from '../_shared/materialSymbols.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
import { marginStyle, num, str, visibilityCss, richtextProseCss, readWeight, } from '../_shared/blockRender.js';
/**
 * Shrine `collapsible-row` — config-by-config. A native `<details>` whose CONTENT panel is a SIBLING div
 * animated with a max-height transition (Shrine's exact no-JS open animation), the caret/plus rotating on
 * open. Container model: renders child blocks via renderBlocks, falling back to its own richtext `content`.
 *
 * Config coverage: visibility, open-by-default, heading + size, leading custom icon, collapse icon
 * (caret/plus) + style (standard/solid) + radius, border type/thickness/style + corner radius, animation
 * speed, the enable_custom_style master gate (paddings + full closed/open colour groups), richtext content,
 * margins.
 *
 * Leading icon (founder 2026-09-11): a Google Material Symbols NAME (`icon`, same `icon-name` field as
 * icon-with-content / icon-with-text — the seller just types the name), `filledIcon` drives the FILL axis,
 * and the `customIcon` image picker OVERRIDES the glyph. Shares {@link MaterialIcon}, so the storefront's
 * `icon_names` webfont subset (collectIconNames) picks the name up like every other block. Deferred: Shopify `page` resource picker (`page` type — platform resource) and the
 * `[description]` liquid token (server-side templating).
 */
export const collapsibleRowBlockSchema = {
    type: 'collapsible-row',
    name: 'Collapsible row',
    max: 8,
    // Shrine's collapsible-row is a CONTAINER (`content_for 'blocks'`). It hosts the generic content blocks;
    // when it has no child blocks it falls back to its own richtext `content` (the simple default).
    accepts: ['text', 'heading', 'image', 'button', 'divider'],
    fields: [
        visibilityField,
        {
            id: 'open',
            type: 'checkbox',
            label: 'Open row by default',
            aiConfigurable: true,
            default: false,
        },
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            default: 'Détails du produit',
            defaultKey: 'product.details',
            // ÉCRIT PAR L'IA : le titre d'un accordéon de fiche produit annonce ce qu'il contient
            // (« Comment l'utiliser », « Livraison »), il n'est pas une étiquette d'interface. Sans ce
            // drapeau, tous les accordéons composés s'appellent « Détails » (25/08).
            aiWritable: true,
            maxLength: 80,
        },
        {
            id: 'headingSize',
            aiConfigurable: true,
            type: 'select',
            label: 'Heading size',
            default: 'medium',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        // ── Icons ─────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'icon',
            type: 'icon-name',
            label: 'Icon',
            group: 'Icons',
            placeholder: 'local_shipping',
            info: 'A Google Material Symbols name (e.g. check_circle, local_shipping). Search on fonts.google.com/icons. Leave empty for no icon.',
        },
        {
            id: 'customIcon',
            type: 'image',
            label: 'Custom icon',
            group: 'Icons',
            info: 'Replaces the Material Symbols icon above.',
        },
        {
            id: 'filledIcon',
            type: 'checkbox',
            label: 'Use filled icon',
            group: 'Icons',
            default: false,
            info: 'Shows the filled version of the icon.',
        },
        // Founder 2026-09-17: the LEADING glyph used to have no colour of its own — it inherited the header
        // text colour, so "colour the icon" meant recolouring the title with it. These three give it its own
        // colour, in BOTH states, and they work whether or not the `enableCustomStyle` suite is on.
        {
            id: 'colorIconSeparately',
            type: 'checkbox',
            label: 'Color the icon separately',
            group: 'Icons',
            default: false,
            info: 'When off, the icon takes the header text color.',
        },
        {
            id: 'iconColor',
            type: 'color',
            label: 'Icon color',
            group: 'Icons',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'colorIconSeparately', equals: true },
        },
        {
            id: 'iconColorOpen',
            type: 'color',
            label: 'Icon color (open row)',
            group: 'Icons',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'colorIconSeparately', equals: true },
        },
        {
            id: 'collapseIcon',
            type: 'select',
            label: 'Collapse icon',
            group: 'Icons',
            default: 'carret',
            options: [
                { value: 'carret', label: 'Caret' },
                { value: 'plus', label: 'Plus' },
            ],
        },
        {
            id: 'collapseIconStyle',
            type: 'select',
            label: 'Collapse icon style',
            group: 'Icons',
            default: 'standard',
            options: [
                { value: 'standard', label: 'Standard' },
                { value: 'solid', label: 'Solid' },
            ],
        },
        {
            id: 'collapseIconRadius',
            type: 'range',
            label: 'Collapse icon corner radius',
            group: 'Icons',
            min: 0,
            max: 100,
            step: 5,
            default: 15,
            unit: '%',
            visibleWhen: { field: 'collapseIconStyle', equals: 'solid' },
        },
        // ── Styling ───────────────────────────────────────────────────────────────────────────────────
        {
            id: 'borderType',
            aiConfigurable: true,
            type: 'select',
            label: 'Border type',
            group: 'Styling',
            default: 'bottom',
            options: [
                { value: 'top-bottom', label: 'Top & bottom' },
                { value: 'bottom', label: 'Bottom' },
                { value: 'all', label: 'All' },
                { value: 'none', label: 'None' },
            ],
        },
        {
            id: 'borderThickness',
            type: 'range',
            label: 'Border thickness',
            group: 'Styling',
            min: 1,
            max: 6,
            step: 1,
            default: 1,
            unit: 'px',
            visibleWhen: { field: 'borderType', notEquals: 'none' },
        },
        {
            id: 'borderStyle',
            type: 'select',
            label: 'Border style',
            group: 'Styling',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'borderType', notEquals: 'none' },
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Styling',
            min: 0,
            max: 40,
            step: 2,
            default: 8,
            unit: 'px',
        },
        {
            id: 'animationSpeed',
            type: 'range',
            label: 'Animation speed',
            group: 'Styling',
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.4,
            unit: 's',
        },
        // ── Colors selection (master gate) ──────────────────────────────────────────────────────────────
        {
            id: 'enableCustomStyle',
            type: 'checkbox',
            label: 'Enable custom colors & padding',
            group: 'Colors selection',
            default: false,
        },
        {
            id: 'headingPaddingVertical',
            type: 'range',
            label: 'Heading padding (vertical)',
            group: 'Colors selection',
            min: 0,
            max: 30,
            step: 2,
            default: 12,
            unit: 'px',
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'headingPaddingHorizontal',
            type: 'range',
            label: 'Heading padding (horizontal)',
            group: 'Colors selection',
            min: 0,
            max: 30,
            step: 2,
            default: 10,
            unit: 'px',
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'contentPaddingVertical',
            type: 'range',
            label: 'Content padding (vertical)',
            group: 'Colors selection',
            min: 0,
            max: 30,
            step: 2,
            default: 10,
            unit: 'px',
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'contentPaddingHorizontal',
            type: 'range',
            label: 'Content padding (horizontal)',
            group: 'Colors selection',
            min: 0,
            max: 30,
            step: 2,
            default: 10,
            unit: 'px',
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        // ── Closed state colors ─────────────────────────────────────────────────────────────────────────
        {
            id: 'closedStateBorder',
            type: 'color',
            label: 'Border',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#dddddd' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'closedStateHeaderBg',
            type: 'color',
            label: 'Header background',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#F9F9F9' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'closedStateHeaderText',
            type: 'color',
            label: 'Header text',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'closedStateCollapseIcon',
            type: 'color',
            label: 'Collapse icon',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: [
                { field: 'enableCustomStyle', equals: true },
                { field: 'collapseIconStyle', notEquals: 'solid' },
            ],
        },
        {
            id: 'closedStateCollapseIconSolid',
            type: 'color',
            label: 'Collapse icon (solid)',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: [
                { field: 'enableCustomStyle', equals: true },
                { field: 'collapseIconStyle', equals: 'solid' },
            ],
        },
        {
            id: 'closedStateCollapseBg',
            type: 'color',
            label: 'Collapse icon background (solid)',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: [
                { field: 'enableCustomStyle', equals: true },
                { field: 'collapseIconStyle', equals: 'solid' },
            ],
        },
        {
            id: 'closedStateContentBg',
            type: 'color',
            label: 'Content background',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#F9F9F9' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'closedStateContentText',
            type: 'color',
            label: 'Content text',
            group: 'Closed state colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        // ── Open state colors ───────────────────────────────────────────────────────────────────────────
        {
            id: 'openColorsOnHover',
            type: 'checkbox',
            label: 'Apply open colors on hover',
            group: 'Open state colors',
            default: true,
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'openStateBorder',
            type: 'color',
            label: 'Border',
            group: 'Open state colors',
            default: { ref: 'custom', value: '#D5AEEA' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'openStateHeaderBg',
            type: 'color',
            label: 'Header background',
            group: 'Open state colors',
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'openStateHeaderText',
            type: 'color',
            label: 'Header text',
            group: 'Open state colors',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'openStateCollapseIcon',
            type: 'color',
            label: 'Collapse icon',
            group: 'Open state colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: [
                { field: 'enableCustomStyle', equals: true },
                { field: 'collapseIconStyle', notEquals: 'solid' },
            ],
        },
        {
            id: 'openStateCollapseIconSolid',
            type: 'color',
            label: 'Collapse icon (solid)',
            group: 'Open state colors',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: [
                { field: 'enableCustomStyle', equals: true },
                { field: 'collapseIconStyle', equals: 'solid' },
            ],
        },
        {
            id: 'openStateCollapseBg',
            type: 'color',
            label: 'Collapse icon background (solid)',
            group: 'Open state colors',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: [
                { field: 'enableCustomStyle', equals: true },
                { field: 'collapseIconStyle', equals: 'solid' },
            ],
        },
        {
            id: 'openStateContentBg',
            type: 'color',
            label: 'Content background',
            group: 'Open state colors',
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        {
            id: 'openStateContentText',
            type: 'color',
            label: 'Content text',
            group: 'Open state colors',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'enableCustomStyle', equals: true },
        },
        // ── Content ─────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'content',
            type: 'richtext',
            label: 'Content',
            group: 'Content',
            default: {
                format: 'html',
                html: '<p>Ajoutez des détails (matière, entretien, livraison…).</p>',
            },
        },
        { ...weightField, group: 'Content' },
        ...marginFields(0, 15),
    ],
};
const HEADING_SIZE = { small: '15px', medium: '17px', large: '20px' };
/** A stored `color` setting is a ColorValue; read its hex (custom) or fall back to a literal. */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
        // 0019 §4a: the THEME branch. Without it a { ref:'theme', token } silently fell through to the
        // frozen fallback, so a seller re-theming their store changed nothing here (allowlisted, finding #18).
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/** Resolve a `customIcon` image setting to its URL (or '' when unset). */
function imageUrl(v) {
    if (v && typeof v === 'object') {
        const img = v;
        if (typeof img.url === 'string')
            return img.url;
        if (typeof img.src === 'string')
            return img.src;
    }
    return '';
}
function CollapseIcon({ isPlus }) {
    return isPlus ? (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", children: _jsx("path", { d: "M7 1v12M1 7h12", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" }) })) : (_jsx("svg", { width: "12", height: "8", viewBox: "0 0 12 8", fill: "none", children: _jsx("path", { d: "M1 1.5 6 6.5l5-5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
const CollapsibleRowBlock = ({ block, context }) => {
    const bs = block.settings;
    const cls = `aqx-cr-${block.id}`;
    const content = bs.content;
    const html = sanitizeRichtextHtml(content?.format === 'html' ? content.html : '');
    // Container model: render child blocks when present, else the simple richtext `content` fallback.
    const children = (block.blocks ?? []);
    const hasChildren = children.some((b) => !b.disabled);
    const isPlus = bs.collapseIcon === 'plus';
    const speed = num(bs.animationSpeed, 0.4);
    const thickness = num(bs.borderThickness, 1);
    const borderType = str(bs.borderType, 'bottom');
    const borderLineStyle = str(bs.borderStyle, 'solid'); // solid | dashed
    const headingPx = HEADING_SIZE[str(bs.headingSize, 'medium')] ?? '17px';
    const cornerRadius = num(bs.cornerRadius, 8);
    const iconSolid = str(bs.collapseIconStyle, 'standard') === 'solid';
    const iconRadius = num(bs.collapseIconRadius, 15);
    const customIconUrl = imageUrl(bs.customIcon);
    // Leading icon: Material Symbols name (typed by the seller), custom image overrides it.
    const iconName = str(bs.icon, '').trim();
    const glyphShown = !customIconUrl && iconRenders(iconName);
    const leadingIcon = customIconUrl || glyphShown;
    const weight = readWeight(bs);
    // Leading glyph: its own colour when the seller asks for one, else it inherits the header text.
    const iconOwnColor = bs.colorIconSeparately === true;
    const leadIconClosed = iconOwnColor ? colorHex(bs.iconColor, '#121212') : '';
    const leadIconOpen = iconOwnColor ? colorHex(bs.iconColorOpen, '#121212') : '';
    // ── Custom-style master gate: paddings + closed/open colour groups ─────────────────────────────
    const customStyle = bs.enableCustomStyle === true;
    const openOnHover = bs.openColorsOnHover !== false; // default true
    const headPadV = customStyle ? num(bs.headingPaddingVertical, 12) : 12;
    const headPadH = customStyle ? num(bs.headingPaddingHorizontal, 10) : 10;
    const contPadV = customStyle ? num(bs.contentPaddingVertical, 10) : 10;
    const contPadH = customStyle ? num(bs.contentPaddingHorizontal, 10) : 10;
    // When custom styling is OFF, fall back to the original scheme-var driven look (unchanged behaviour).
    const c = {
        closedBorder: customStyle
            ? colorHex(bs.closedStateBorder, '#dddddd')
            : 'var(--aqx-scheme-border)',
        closedHeaderBg: customStyle ? colorHex(bs.closedStateHeaderBg, '#F9F9F9') : 'transparent',
        closedHeaderText: customStyle
            ? colorHex(bs.closedStateHeaderText, '#121212')
            : 'var(--aqx-scheme-text)',
        closedCollapseIcon: customStyle
            ? colorHex(iconSolid ? bs.closedStateCollapseIconSolid : bs.closedStateCollapseIcon, iconSolid ? '#ffffff' : '#121212')
            : 'var(--aqx-scheme-muted)',
        closedCollapseBg: customStyle ? colorHex(bs.closedStateCollapseBg, '#121212') : 'transparent',
        closedContentBg: customStyle ? colorHex(bs.closedStateContentBg, '#F9F9F9') : 'transparent',
        closedContentText: customStyle
            ? colorHex(bs.closedStateContentText, '#121212')
            : 'var(--aqx-scheme-text)',
        openBorder: customStyle ? colorHex(bs.openStateBorder, '#D5AEEA') : 'var(--aqx-scheme-border)',
        openHeaderBg: customStyle ? colorHex(bs.openStateHeaderBg, '#F5FBFF') : 'transparent',
        openHeaderText: customStyle
            ? colorHex(bs.openStateHeaderText, '#000000')
            : 'var(--aqx-scheme-text)',
        openCollapseIcon: customStyle
            ? colorHex(iconSolid ? bs.openStateCollapseIconSolid : bs.openStateCollapseIcon, iconSolid ? '#ffffff' : '#121212')
            : 'var(--aqx-scheme-muted)',
        openCollapseBg: customStyle
            ? colorHex(bs.openStateCollapseBg, 'var(--aqx-theme-accent)')
            : 'transparent',
        openContentBg: customStyle ? colorHex(bs.openStateContentBg, '#F5FBFF') : 'transparent',
        openContentText: customStyle
            ? colorHex(bs.openStateContentText, '#121212')
            : 'var(--aqx-scheme-text)',
    };
    const padInline = borderType === 'all' && !customStyle ? '10px' : '0px';
    // Outer border per Shrine border_type. The LINE is emitted as a CSS rule, not inline: the open-state
    // `border-color` rule has to be able to win, and an inline declaration beats every rule in our own
    // <style> (17/09 — that is why the whole "Open state colors" group did nothing). Radius/overflow are
    // state-independent, so they stay inline.
    const borderLine = `${thickness}px ${borderLineStyle} ${c.closedBorder}`;
    const borderStyle = {};
    if (borderType === 'all') {
        borderStyle.borderRadius = `${cornerRadius}px`;
        borderStyle.overflow = 'hidden';
    }
    else if (borderType === 'none' && cornerRadius > 0) {
        borderStyle.borderRadius = `${cornerRadius}px`;
        borderStyle.overflow = 'hidden';
    }
    const borderCss = borderType === 'bottom'
        ? `.${cls}{border-bottom:${borderLine}}`
        : borderType === 'top-bottom'
            ? `.${cls}{border-top:${borderLine};border-bottom:${borderLine}}`
            : borderType === 'all'
                ? `.${cls}{border:${borderLine}}`
                : '';
    const summaryStyle = {
        cursor: 'pointer',
        listStyle: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        padding: `${headPadV}px ${customStyle ? `${headPadH}px` : padInline}`,
        fontFamily: 'var(--aqx-theme-heading-font, var(--aqx-theme-font))',
        fontSize: headingPx,
        fontWeight: 600,
    };
    const contentStyle = {
        padding: customStyle ? `${contPadV}px ${contPadH}px` : `0 ${padInline} ${contPadV}px`,
        fontSize: 'var(--aqx-theme-text-base, 16px)',
        lineHeight: 'var(--aqx-theme-lh-relaxed, 1.7)',
        fontWeight: weight || undefined,
    };
    // Solid collapse-icon chip: colored bg + radius, icon recolored to the solid icon color.
    const iconChipStyle = iconSolid
        ? { borderRadius: `${iconRadius}%`, padding: '4px', display: 'inline-flex' }
        : { display: 'inline-flex' };
    const heading = (_jsxs(_Fragment, { children: [_jsxs("span", { className: `${cls}__label`, style: { display: 'inline-flex', alignItems: 'center', gap: '8px' }, children: [leadingIcon ? (_jsx("span", { className: `${cls}__leadicon`, style: { flex: '0 0 auto', lineHeight: 0 }, "aria-hidden": "true", children: _jsx(MaterialIcon, { name: iconName, filled: bs.filledIcon === true, image: customIconUrl ? { url: customIconUrl } : null, size: 20 }) })) : null, _jsx("span", { children: str(bs.heading, t(context, 'product.details')) })] }), _jsx("span", { className: `${cls}__icon`, "aria-hidden": "true", style: iconChipStyle, children: _jsx(CollapseIcon, { isPlus: isPlus }) })] }));
    // Native <details>, default CLOSED (unless `open`), SAME in builder + storefront so it's always
    // openable/closable on click. The content panel is a SIBLING animated with a bulletproof max-height
    // transition that collapses FULLY to 0 when closed — no peeking sliver — + caret/plus rotation.
    // Open-state colors are applied when the <details> is [open], and (optionally) on hover.
    const hoverSel = openOnHover ? `,.${cls}:hover>details>summary` : '';
    const hoverIconSel = openOnHover ? `,.${cls}:hover>details>summary .${cls}__icon` : '';
    const hoverLeadSel = openOnHover ? `,.${cls}:hover>details>summary .${cls}__leadicon` : '';
    // The CLOSED colours are rules too, not inline styles — see the note on `borderCss`. Their selectors
    // are one step LESS specific than the open ones (`details` vs `details[open]`), so the open state wins
    // by the cascade alone, in whatever order the two strings are concatenated.
    const closedColorCss = `.${cls} details>summary{background:${c.closedHeaderBg};color:${c.closedHeaderText}}` +
        `.${cls} details>summary .${cls}__icon{color:${c.closedCollapseIcon}}` +
        (iconSolid ? `.${cls} details>summary .${cls}__icon{background:${c.closedCollapseBg}}` : '') +
        `.${cls} .${cls}__wrap>div{background:${c.closedContentBg};color:${c.closedContentText}}`;
    const openColorCss = customStyle
        ? `.${cls} details[open]>summary${hoverSel}{background:${c.openHeaderBg};color:${c.openHeaderText}}` +
            `.${cls} details[open]>summary .${cls}__icon${hoverIconSel}{color:${c.openCollapseIcon}}` +
            (iconSolid
                ? `.${cls} details[open]>summary .${cls}__icon${hoverIconSel}{background:${c.openCollapseBg}}`
                : '') +
            `.${cls} details[open] + .${cls}__wrap>div{background:${c.openContentBg};color:${c.openContentText}}` +
            (borderType !== 'none'
                ? `.${cls}:has(details[open])${openOnHover ? `,.${cls}:hover` : ''}{border-color:${c.openBorder}}`
                : '')
        : '';
    // The leading glyph's own colour. Its selector reaches one element deeper than the header-text rule,
    // so it overrides the colour the glyph would otherwise inherit from the title.
    const leadIconCss = iconOwnColor
        ? `.${cls} details>summary .${cls}__leadicon{color:${leadIconClosed}}` +
            `.${cls} details[open]>summary .${cls}__leadicon${hoverLeadSel}{color:${leadIconOpen}}`
        : '';
    const css = `.${cls}__wrap{overflow:hidden;max-height:0;transition:max-height ${speed}s ease-in-out}` +
        `.${cls} details[open] + .${cls}__wrap{max-height:1500px}` +
        `.${cls}__icon{transition:transform ${speed}s ease-in-out}` +
        `.${cls} details[open] .${cls}__icon{transform:rotate(${isPlus ? 135 : 180}deg)}` +
        `.${cls} summary::-webkit-details-marker{display:none}` +
        // Restore list markers / heading sizes / underline in the richtext content panel (Preflight strips them).
        richtextProseCss(`${cls}__wrap`) +
        borderCss +
        closedColorCss +
        openColorCss +
        leadIconCss +
        visibilityCss(cls, bs.visibility);
    return (_jsxs("div", { className: cls, style: { ...borderStyle, ...marginStyle(bs, 0, 15) }, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), glyphShown ? _jsx(MaterialSymbolsFont, {}) : null, _jsx("details", { ...(bs.open === true ? { open: true } : {}), children: _jsx("summary", { style: summaryStyle, children: heading }) }), _jsx("div", { className: `${cls}__wrap`, children: hasChildren ? (_jsx("div", { style: contentStyle, children: renderBlocks(children, context) })) : (_jsx("div", { style: contentStyle, dangerouslySetInnerHTML: { __html: html } })) })] }));
};
export const collapsibleRowBlock = {
    type: 'collapsible-row',
    schema: collapsibleRowBlockSchema,
    Component: CollapsibleRowBlock,
    meta: { label: 'Collapsible row' },
};
//# sourceMappingURL=index.js.map